import { graphql } from '@/gql'
import type { GalleyOrderHistoryQuery } from '@/gql/graphql'
import { gqlRequest, type RequestSession } from './api/client'
import { recordDate } from './records'
import type { Slip } from './slips'

export const OrderHistoryQuery = graphql(`
  query GalleyOrderHistory($restaurantId: ID!, $start: DateTime!, $end: DateTime!, $after: String) {
    restaurant(id: $restaurantId) { id
      orders(filter: { createdAt: { start: $start, end: $end } }, first: 100, after: $after) {
        edges { node {
          id createdAt finishedAt readyForPickupAt pickedUpAt deliveryTime estimatedDeliveryTime transactionChannel type userMessage
          receipt { humanOrderCode }
          items { id count message product { id name }
            alternatives { id name count isChosen isDefault addonProduct { id name } alternativeGroup { id } }
          }
        } }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`)
type NativeOrder = NonNullable<NonNullable<GalleyOrderHistoryQuery['restaurant']>['orders']>['edges'][number]['node']
/** Native history uses the order's saved completion timestamps; Galley never records ticket times. */
export function historyRows(orders: NativeOrder[], date: string) {
  return orders
    .filter((order) => order.createdAt && recordDate(Date.parse(order.createdAt)) === date && order.finishedAt)
    .map((order) => ({
      slipId: `history:${order.id}`,
      orderCode: order.receipt?.humanOrderCode ?? order.id,
      completedAt: order.finishedAt ?? '',
      seconds: ticketSeconds(order.createdAt, order.deliveryTime, order.finishedAt),
      pickedUpAt: order.pickedUpAt,
      canRecall: Boolean(order.pickedUpAt || !order.readyForPickupAt),
      slip: {
        id: `history:${order.id}`,
        createdAt: order.createdAt,
        humanOrderCode: order.receipt?.humanOrderCode,
        deliveryTime: order.deliveryTime,
        estimatedDeliveryTime: order.estimatedDeliveryTime,
        transactionChannel: order.transactionChannel,
        orderType: order.type,
        message: order.userMessage,
        lineItemSections: [
          {
            id: 'history',
            isSecondary: false,
            items: order.items?.map((item) => ({
              id: item.id,
              count: item.count,
              message: item.message,
              productId: item.product?.id,
              name: item.product?.name,
              alternativeItems: item.alternatives?.map((alternative) => ({
                id: alternative.id,
                name: alternative.name,
                count: alternative.count,
                isChosen: alternative.isChosen,
                isDefault: alternative.isDefault,
                productId: alternative.addonProduct?.id,
                alternativeGroupId: alternative.alternativeGroup?.id,
              })),
            })),
          },
        ],
      } satisfies Slip,
    }))
}
export async function loadOrderHistory(session: RequestSession, restaurantId: string, date: string) {
  // Fetch a UTC envelope then select the restaurant-local creation date, including DST changes.
  const day = Date.parse(`${date}T00:00:00Z`)
  const start = new Date(day - 86400000).toISOString()
  const end = new Date(day + 2 * 86400000).toISOString()
  const orders: NativeOrder[] = []
  let after: string | undefined
  do {
    const data = await gqlRequest(session, OrderHistoryQuery, { restaurantId, start, end, after })
    const page = data.restaurant?.orders
    if (!page) throw new Error('Restaurangens beställningar kunde inte läsas')
    orders.push(...page.edges.map((edge) => edge.node))
    after = page.pageInfo.hasNextPage ? (page.pageInfo.endCursor ?? undefined) : undefined
  } while (after)
  return historyRows(orders, date)
}

export function ticketSeconds(createdAt: string | null, deliveryTime: string | null, finishedAt: string | null): number {
  const created = Date.parse(createdAt ?? '')
  const delivery = Date.parse(deliveryTime ?? '')
  const finished = Date.parse(finishedAt ?? '')
  return Number.isFinite(created) && Number.isFinite(finished) ? Math.max(0, Math.floor((finished - Math.max(created, Number.isFinite(delivery) ? delivery : created)) / 1000)) : 0
}
